import { useCallback, useContext, useEffect, useState } from 'react'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from '@/components/Filter/ClientFields/SelectClientFilter'
import {
  SelectTaModalOpenEvent,
  SelectTradingAccountFilter,
} from '@/components/Filter/TradingAccountFields/SelectTradingAccountFilter'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from '@/components/Filter/WalletFields/SelectWalletFilter'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PageData } from 'api/Pagination/Types'
import {
  AdjustmentTransactionDto,
  AdjustmentTransactionFilterKey,
} from 'api/Transaction/Types/AdjustmentTransactionDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import { SelectAssigneeFilter } from 'components/Filter/ClientFields/SelectAssigneeFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getWalletTradingAccountFields } from 'components/Filter/walletTradingAccountFields'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { sleep } from 'utils/sleep'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'
import { isTransactionCompletedStatus } from 'utils/transaction/status.index'

import { CustomColumnMenu } from '../Records/Clients/CustomColumnMenu'
import { SelectClientEvent, SelectClientTable } from '../Records/Clients/SelectClientTable'
import SelectTradingAccountTable, {
  SelectTradingAccountEvent,
} from '../Records/TradingAccounts/SelectTradingAccountTable'
import SelectWalletTable, { SelectWalletEvent } from '../Records/Wallets/SelectWalletTable'
import { normalizeTransactionsSortQuery } from './normalizer'
import {
  SelectTransactionsAssignTable,
  SelectTransactionsEvent,
} from './SelectTransactionAssignTable'
import TransactionReversePage from './TransactionReverse/TransactionReversePage'
import walletsTransactionAccountColumns, { GridColumn } from './walletsTAColumn'

import './Create/transaction.scss'

import { MultiWalletTypeSelectFilter, PlatformFilter } from '@/components/Filter/common.filter'
import { fetchPlatformTypesAction } from '@/store/trading-accounts/trading-accounts.actions'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GridFilterActionState,
  selectGridFilterByFilterId,
} from 'store/grid'
import {
  fetchTransactionStatusAction,
  fetchTransactionTypeAction,
} from 'store/transactions/transaction.action'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'

const WalletTradingAccountTransferOverview = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const dispatch = useAppDispatch()
  const permissions = auth?.permissions || []
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [transactions, setTransactions] = useState<AdjustmentTransactionDto[]>([])
  const [columns, setColumns] = useState<GridColumn[]>([])

  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [isAssigneeModalOpen, setAssigneeModalOpen] = useState<boolean>(false)
  const [isTradingAccountModalOpen, setTradingAccountModalOpen] = useState<boolean>(false)

  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()
  const [assignModalFilterId, setAssignModalFilterId] = useState<string>()
  const [tradingAccountsModalFilterId, setTradingAccountsModalFilterId] = useState<string>()

  const [reverseTransactionModal, setReverseTransactionModal] = useState<{
    data?: TransactionDto
    visible: boolean
  }>({ data: undefined, visible: false })

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const gridFilterByFilterIdSelector = useAppSelector(selectGridFilterByFilterId)
  const transactionType = useAppSelector((state) => state.transactions.types)
  const transactionStatus = useAppSelector((state) => state.transactions.status)

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<AdjustmentTransactionDto[]>> => {
          const searchQuery = getSearchQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await walletsApiClient.getPlatformTransferTransactions({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(transactionType)) dispatch(fetchTransactionTypeAction(walletsApiClient))
          if (isEmpty(transactionStatus)) dispatch(fetchTransactionStatusAction(walletsApiClient))
          if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))

          setColumns(walletsTransactionAccountColumns)
          if (response?.items) {
            setTransactions(response.items)
          }
          setTableData({ ...tableData, meta: response })

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<AdjustmentTransactionDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTransactionsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const searchQuery = query?.search !== undefined ? query.search : getSearchQuery()
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await walletsApiClient.getPlatformTransferTransactions({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTransactions(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getSearchQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await walletsApiClient.getPlatformTransferTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getPlatformTransferTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    gridStatusActions.setFetching(false)
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await walletsApiClient.getPlatformTransferTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    if (response?.items) {
      setTransactions(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    gridStatusActions.setFetching(true)
    const searchQuery = getSearchQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page

    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await walletsApiClient.getPlatformTransferTransactions({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTransactions(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleFilterFetch = async (query?: PageQuery) => {
    await filterFetch(query)
  }

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
    setClientModalFilterId(undefined)
  }

  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
    setWalletModalFilterId(undefined)
  }

  const handleAssigneeModalOpen = (event: SelectWalletModalOpenEvent) => {
    setAssigneeModalOpen(true)
    setAssignModalFilterId(event.filterId)
  }

  const handleSelectAssignUsersModalClose = () => {
    setAssigneeModalOpen(false)
    setAssignModalFilterId(undefined)
  }

  const handleTradingAccountModalOpen = (event: SelectTaModalOpenEvent) => {
    setTradingAccountModalOpen(true)
    setTradingAccountsModalFilterId(event.filterId)
  }

  const handleTradingAccountModalClose = () => {
    setTradingAccountModalOpen(false)
    setTradingAccountsModalFilterId(undefined)
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedUsersAssign = (event: SelectTransactionsEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
      setAssigneeModalOpen(false)
    }
  }

  const handleSelectedTradingAccounts = (event: SelectTradingAccountEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleReverseTransaction = (transaction: TransactionDto) => {
    setReverseTransactionModal({ data: transaction, visible: true })
  }

  const handleReverseCompleteTransactionClose = async () => {
    setReverseTransactionModal({ data: undefined, visible: false })
    await sleep(1000)
    await filterFetch()
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isAssigneeModalOpen && (
        <Dialog
          open={isAssigneeModalOpen}
          onOpenChange={(open) => !open && handleSelectAssignUsersModalClose()}
        >
          <SelectTransactionsAssignTable
            filterId={assignModalFilterId}
            onMultipleSelect={handleSelectedUsersAssign}
            selectText='Select'
          />
        </Dialog>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      {isTradingAccountModalOpen && (
        <Modal
          width={900}
          visible={isTradingAccountModalOpen}
          onClose={handleTradingAccountModalClose}
        >
          <SelectTradingAccountTable
            filterId={tradingAccountsModalFilterId}
            onSelect={handleSelectedTradingAccounts}
            onClose={handleTradingAccountModalClose}
          />
        </Modal>
      )}
      {reverseTransactionModal.visible && !!reverseTransactionModal.data && (
        <Modal
          width={600}
          padding='3rem'
          visible={reverseTransactionModal.visible}
          onClose={handleReverseCompleteTransactionClose}
        >
          <TransactionReversePage
            transaction={reverseTransactionModal.data}
            onClose={handleReverseCompleteTransactionClose}
          />
        </Modal>
      )}
      <Breadcrumbs
        crumbs={[{ title: 'Transactions - Platform', link: links.transactionPlatform.url }]}
      />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={handleFilterFetch}
        filterType={FilterType.WALLET_TRADING_ACCOUNTS}
        filterFields={[
          {
            name: AdjustmentTransactionFilterKey.client,
            label: 'Client',
            filter: (clientFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: AdjustmentTransactionFilterKey.wallet,
            label: 'Wallet',
            filter: (walletFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: AdjustmentTransactionFilterKey.walletType,
            label: 'Wallet Type',
            filter: MultiWalletTypeSelectFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: AdjustmentTransactionFilterKey.platform,
            label: 'Platform Type',
            filter: PlatformFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: AdjustmentTransactionFilterKey.tradingAccount,
            label: 'Trading Account',
            filter: (taFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectTradingAccountFilter
                {...taFilter}
                onTaModalOpen={handleTradingAccountModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: AdjustmentTransactionFilterKey.assignee,
            label: 'Assignee',
            filter: (assigneeFilter: {
              filter: FilterDescriptor
              onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
            }) => (
              <SelectAssigneeFilter
                {...assigneeFilter}
                onAssignModalOpen={handleAssigneeModalOpen}
              />
            ),
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          ...getWalletTradingAccountFields(),
        ]}
        getQuery={getSearchQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport
          data={transactions}
          hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportTransactionsModule)}
        >
          <Grid
            {...gridState}
            resizable
            data={transactions}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(props) => (
                    <CustomColumnMenu
                      {...props}
                      columns={columns}
                      onColumnsSubmit={(columnsState: GridColumn[]) => setColumns(columnsState)}
                    />
                  )}
                  {...column}
                />
              ))}
            <Column
              width={60}
              title=''
              locked
              cell={(gridProps: GridCellProps) => {
                if (isTransactionCompletedStatus(gridProps.dataItem.state.id)) {
                  return (
                    <td className={gridProps.className} style={gridProps.style}>
                      <MoreButton
                        items={[
                          {
                            name: 'Reverse',
                            onClick: () => handleReverseTransaction(gridProps.dataItem),
                          },
                        ]}
                      />
                    </td>
                  )
                }

                return <td className={gridProps.className} style={gridProps.style} />
              }}
            />
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}

export default WalletTradingAccountTransferOverview
